import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Stack from 'components/core/Stack';
import Typography from 'components/core/Typography';
import Sheet from 'components/core/Sheet';
import Card from 'components/core/Card';
import { CircularProgress } from '@mui/joy';

import TaxonomyForm from 'components/TaxonomyForm';
import taxonomyActions from 'actions/taxonomyActions';
import taxonomyNameActions from 'actions/taxonomyNameActions';
import alternateIdentifierActions from 'actions/alternateIdentifierActions';
import lookupTableActions from 'actions/lookupTableActions';

const TaxonomyEdit = () => {
    const { taxonomyId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const options = useSelector(({ lookupTable }) => lookupTable.options);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [taxonomyData, setTaxonomyData] = useState(null);
    const [taxonomies, setTaxonomies] = useState([]);

    useEffect(() => {
        fetchTaxonomyData();
        fetchTaxonomies();
        dispatch(lookupTableActions.fetchAllOptions());
    }, [taxonomyId]);

    const fetchTaxonomyData = async () => {
        setLoading(true);
        setError(null);
        try {
            const query = new Parse.Query('Taxonomy');
            query.include(['taxonomyType', 'taxonomyCategory', 'parent', 'primarySystem', 
                           'subject', 'reproduction', 'nativityType', 'invasiveStatus', 
                           'securityType', 'protectedStatus', 'sRank', 'rigcn', 'rinhsStatus']);
            
            const taxonomy = await query.get(taxonomyId);
            
            const [namesResponse, altIdsResponse] = await Promise.all([
                dispatch(taxonomyNameActions.listSynonyms(taxonomy)),
                dispatch(alternateIdentifierActions.listByTaxonomy(taxonomyId))
            ]);
            
            const data = {
                taxonomy: taxonomy,
                names: namesResponse.value || [],
                alternateIdentifiers: altIdsResponse.value || []
            };
            
            setTaxonomyData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchTaxonomies = async () => {
        try {
            const response = await dispatch(taxonomyActions.list({ limit: 1000 }));
            setTaxonomies(response.value || []);
        } catch (err) {
            console.error('Failed to fetch taxonomies:', err);
        }
    };

    const handleSubmit = (result) => {
        const idToUse = result.taxonomy ? result.id : result.id;
        navigate(`/taxonomy/${idToUse}`);
    };

    if (loading) {
        return (
            <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <CircularProgress />
            </Stack>
        );
    }

    if (error) {
        return (
            <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <Typography level="h4" color="danger">Error loading taxonomy</Typography>
                <Typography>{error}</Typography>
            </Stack>
        );
    }

    return (
        <Sheet sx={{ p: 4 }}>
            <Card>
                <Stack spacing={4}>
                    <Typography variant="h4">Edit Taxonomy</Typography>
                    {taxonomyData && (
                        <TaxonomyForm
                            options={options}
                            onSubmit={handleSubmit}
                            taxonomies={taxonomies}
                            initialValues={taxonomyData}
                        />
                    )}
                </Stack>
            </Card>
        </Sheet>
    );
};

export default TaxonomyEdit;